'use client';

import { useCallback, useEffect, useState } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Input } from '@kit/ui/input';
import { Label } from '@kit/ui/label';
import { cn } from '@kit/ui/utils';

import { normalizeSocialHandle } from '../schemas/dancer-form.schema';
import {
  SOCIAL_BRAND_ICONS,
  type SocialPlatform,
} from './icons/social-brand-icons';

export type { SocialPlatform } from './icons/social-brand-icons';

const DEBOUNCE_DELAY = 300; // ms

interface SocialProfile {
  handle: string;
  profilePictureUrl: string;
  displayName: string;
  verified: boolean;
}

interface PlatformConfig {
  name: string;
  /** Muted prefix shown before the input, e.g. `instagram.com/`. */
  urlPrefix: string;
  /** Builds the public profile URL for a bare handle. */
  profileUrl: (handle: string) => string;
  iconColor: string;
  /**
   * Whether the handle can be checked against a live oEmbed lookup
   * (`/api/social/verify-handle`) to surface an avatar + verified badge.
   * Only Instagram / TikTok / Facebook / YouTube have a usable public
   * endpoint; the rest are format-checked and get an open-profile link only.
   */
  verify: boolean;
}

const PLATFORMS: Record<SocialPlatform, PlatformConfig> = {
  instagram: {
    name: 'Instagram',
    urlPrefix: 'instagram.com/',
    profileUrl: (h) => `https://instagram.com/${h}`,
    iconColor: 'text-pink-600 dark:text-pink-400',
    verify: true,
  },
  tiktok: {
    name: 'TikTok',
    urlPrefix: 'tiktok.com/@',
    profileUrl: (h) => `https://tiktok.com/@${h}`,
    iconColor: 'text-foreground',
    verify: true,
  },
  facebook: {
    name: 'Facebook',
    urlPrefix: 'facebook.com/',
    profileUrl: (h) => `https://facebook.com/${h}`,
    iconColor: 'text-blue-600 dark:text-blue-400',
    verify: true,
  },
  youtube: {
    name: 'YouTube',
    urlPrefix: 'youtube.com/@',
    profileUrl: (h) => `https://youtube.com/@${h}`,
    iconColor: 'text-red-600 dark:text-red-400',
    verify: true,
  },
  vimeo: {
    name: 'Vimeo',
    urlPrefix: 'vimeo.com/',
    profileUrl: (h) => `https://vimeo.com/${h}`,
    iconColor: 'text-sky-600 dark:text-sky-400',
    verify: false,
  },
  x: {
    name: 'X',
    urlPrefix: 'x.com/',
    profileUrl: (h) => `https://x.com/${h}`,
    iconColor: 'text-foreground',
    verify: false,
  },
  pinterest: {
    name: 'Pinterest',
    urlPrefix: 'pinterest.com/',
    profileUrl: (h) => `https://pinterest.com/${h}`,
    iconColor: 'text-red-700 dark:text-red-500',
    verify: false,
  },
  snapchat: {
    name: 'Snapchat',
    urlPrefix: 'snapchat.com/add/',
    profileUrl: (h) => `https://snapchat.com/add/${h}`,
    iconColor: 'text-yellow-500',
    verify: false,
  },
};

export interface SocialHandleInputProps {
  platform: SocialPlatform;
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  disabled?: boolean;
  /** Optional label override; defaults to the translated platform label. */
  label?: string;
  /**
   * Render the built-in `<Label>`. Set false when an outer wrapper already
   * provides one (e.g. a shadcn `FormLabel` in an admin `FormField`) to avoid
   * a duplicate label.
   */
  showLabel?: boolean;
  /**
   * Gate the live oEmbed verify/avatar lookup (verify-capable platforms only).
   * Default true. Set false in high-volume or unauthenticated contexts (e.g.
   * the public competition apply form) to keep the icon/prefix/normalize/
   * link-out UX without firing `/api/social/verify-handle` per keystroke-pause.
   */
  enableVerification?: boolean;
  id?: string;
}

/**
 * The single reusable editor for a social media handle. Renders a brand icon +
 * `platform.com/` prefix, normalizes a pasted profile URL down to the bare
 * handle (via {@link normalizeSocialHandle}), surfaces the resolved profile as
 * an open-in-new-tab link, and — for the four platforms with a public oEmbed
 * endpoint — shows a debounced avatar + verified badge.
 *
 * Every social-handle field in the app MUST use this component; the
 * `eslint-plugin-social-handle` rule fails the build on a raw `<Input>` bound
 * to a `*Handle` / `*_handle` field.
 */
export function SocialHandleInput({
  platform,
  value = '',
  onChange,
  onBlur,
  error,
  disabled,
  label,
  showLabel = true,
  enableVerification = true,
  id,
}: SocialHandleInputProps) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(value);
  const [isVerifying, setIsVerifying] = useState(false);
  const [profile, setProfile] = useState<SocialProfile | null>(null);
  const [verificationError, setVerificationError] = useState<string | null>(
    null,
  );

  const config = PLATFORMS[platform];
  const Icon = SOCIAL_BRAND_ICONS[platform];
  const fieldId = id ?? `${platform}-handle`;
  const resolvedLabel =
    label ??
    t(`dancer:socialHandleInput.${platform}.label`, {
      defaultValue: config.name,
    });

  // Keep the local input in sync when the form value changes externally
  // (defaultValues load, form.reset after save). React's "adjust state during
  // render" pattern rather than an effect, per react-hooks/set-state-in-effect.
  const [lastExternalValue, setLastExternalValue] = useState(value);
  if (value !== lastExternalValue) {
    setLastExternalValue(value);
    setInputValue(value);
  }

  const verifyHandle = useCallback(
    async (handle: string) => {
      if (
        !enableVerification ||
        !config.verify ||
        !handle ||
        handle.trim() === ''
      ) {
        setProfile(null);
        setVerificationError(null);
        return;
      }

      setIsVerifying(true);
      setVerificationError(null);

      try {
        const response = await fetch('/api/social/verify-handle', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ platform, handle }),
        });

        if (!response.ok) {
          const err = await response.json();
          setVerificationError(
            err.error || t('dancer:socialHandleInput.verifyFailed'),
          );
          setProfile(null);
          return;
        }

        const data: SocialProfile = await response.json();
        setProfile(data);
      } catch {
        setVerificationError(t('dancer:socialHandleInput.verifyError'));
        setProfile(null);
      } finally {
        setIsVerifying(false);
      }
    },
    [platform, config.verify, enableVerification, t],
  );

  // Debounced propagation + verification.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputValue !== value) onChange(inputValue);
      verifyHandle(inputValue);
    }, DEBOUNCE_DELAY);
    return () => clearTimeout(timer);
  }, [inputValue, value, onChange, verifyHandle]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Strip a leading @ live; full URL-paste normalization runs on blur so it
    // doesn't fight the user mid-type.
    setInputValue(e.target.value.replace(/^@/, ''));
    setProfile(null);
  };

  // On blur, collapse a pasted profile URL to the bare handle so what the user
  // sees matches what the schema stores (single normalization source).
  const handleBlur = () => {
    const normalized = normalizeSocialHandle(inputValue, platform) ?? '';
    if (normalized !== inputValue) setInputValue(normalized);
    onBlur?.();
  };

  const trimmed = inputValue.trim();
  const hasError = Boolean(error || verificationError);

  return (
    <div className="space-y-2">
      {showLabel && <Label htmlFor={fieldId}>{resolvedLabel}</Label>}

      <div
        className={cn(
          'border-input focus-within:border-ring focus-within:ring-ring/50 flex h-9 w-full items-center rounded-md border bg-transparent text-sm shadow-xs transition-[color,box-shadow] focus-within:ring-[3px]',
          hasError &&
            'border-destructive focus-within:border-destructive focus-within:ring-destructive/20',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        <span className="text-muted-foreground flex shrink-0 items-center gap-1.5 pl-3">
          <Icon className={cn('h-4 w-4', config.iconColor)} />
          <span className="hidden text-xs sm:inline">{config.urlPrefix}</span>
        </span>
        <Input
          id={fieldId}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          placeholder={t(`dancer:socialHandleInput.${platform}.placeholder`, {
            defaultValue: 'your.handle',
          })}
          disabled={disabled}
          aria-describedby={`${fieldId}-help`}
          aria-invalid={hasError}
          className="h-8 border-0 bg-transparent px-1.5 shadow-none focus-visible:ring-0"
        />
        <span className="flex shrink-0 items-center gap-1 pr-2">
          {isVerifying && (
            <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
          )}
          {!isVerifying && trimmed && (
            <a
              href={config.profileUrl(trimmed)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground rounded p-1"
              aria-label={t('dancer:socialHandleInput.openProfile', {
                platform: config.name,
                defaultValue: 'Open {{platform}} profile',
              })}
              tabIndex={-1}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </span>
      </div>

      {/* Verified profile preview (verify platforms only). */}
      {profile && !isVerifying && (
        <div className="border-border bg-muted/50 flex items-center gap-3 rounded-lg border p-3">
          {/* eslint-disable-next-line ballee-media/no-untransformed-media, @next/next/no-img-element -- oEmbed avatar from an external social host (not in next.config remotePatterns), never R2; cdnImageUrl would pass it through unchanged. */}
          <img
            src={profile.profilePictureUrl}
            alt={t('dancer:socialHandleInput.profileAlt', {
              name: profile.displayName,
            })}
            className="border-border h-10 w-10 rounded-full border-2"
          />
          <div className="flex-1">
            <p className="text-sm font-medium">
              {profile.displayName}
              {profile.verified && (
                <span className="ml-1 text-blue-500">✓</span>
              )}
            </p>
            <p className="text-muted-foreground text-xs">@{profile.handle}</p>
          </div>
          <div className={cn('text-xs font-medium', config.iconColor)}>
            {config.name}
          </div>
        </div>
      )}

      {/* Help text */}
      {!hasError && (
        <p id={`${fieldId}-help`} className="text-muted-foreground text-xs">
          {t(`dancer:socialHandleInput.${platform}.helpText`, {
            defaultValue: 'Paste your profile link or type your handle.',
          })}
        </p>
      )}

      {/* Errors */}
      {hasError && (
        <p className="text-destructive text-xs">{error || verificationError}</p>
      )}
    </div>
  );
}
